import { useEffect, useState } from 'react'
import { Accordion, ActionIcon, Alert, Button, Menu, Modal, Popover, ScrollArea, SegmentedControl, Select, Switch, TextInput, Tooltip } from '@mantine/core'
import { IconBookmark, IconDots, IconPencil, IconPlus, IconTrash, IconLock, IconUsers } from '@tabler/icons-react'
import useSWR from 'swr'
import useNavigationViews from '@/hooks/useNavigationViews'
import useMe from '@/hooks/useMe'
import { useAxiosApi } from '@/hooks/useAxiosApi'
import { sameTableView, tableViewConfiguration, compareTableViews } from '@/lib/tableViews.mjs'
import TableColumns from './TableColumns'
import TableViewOrder from './TableViewOrder'
import { orderedTableColumns } from '@/lib/tableColumns.mjs'
import TableViewFilters from './TableViewFilters'
import LocationSelect from '../LocationSelect'

const SAVED_TABLES = [
    'components', 'component-invlevels-history', 'production-orders', 'fulfillment-orders/table',
    'fulfillments', 'warranties', 'products', 'shipments', 'purchase-orders', 'profiles',
    'shopify/products', 'shopify/product-variants', 'retailer-invoices', 'retailers', 'retailer-estimates',
]
const PARTNER_TABLES = ['components', 'component-invlevels-history', 'production-orders', 'fulfillment-orders/table', 'fulfillments']

export default function TableViews(props) {
    const { id: userId, role } = useMe()
    return <TableViewManager key={`${props.tableKey}:${userId}:${role}`} {...props} userId={userId} role={role} />
}

function TableViewManager({ tableKey, userId, role, withLocation, currentLocation, availableColumns = [], availableFilters = [], tableState, selectView }) {
    const { mutate: refreshNavigation } = useNavigationViews()
    const [ordering, setOrdering] = useState(null)
    const [orderBaseline, setOrderBaseline] = useState(null)
    const [savingOrder, setSavingOrder] = useState(false)
    const [actionMenuId, setActionMenuId] = useState(null)
    const [opened, setOpened] = useState(false)
    const [modalOpened, setModalOpened] = useState(false)
    const [editor, setEditor] = useState(null)
    const [deleting, setDeleting] = useState(null)
    const [selected, setSelected] = useState(null)
    const [busy, setBusy] = useState(false)
    const [stale, setStale] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const canSave = SAVED_TABLES.includes(tableKey) && (role === 'ADMIN' || (role === 'MNP' && PARTNER_TABLES.includes(tableKey)))
    const { requestGet, requestPost, requestPatch, requestDelete } = useAxiosApi()
    const { data: saved = [], error, isLoading, mutate } = useSWR(
        canSave && userId ? ['table-views', tableKey, userId, role] : null,
        async () => (await requestGet('/table-views', { table_key: tableKey })).data
    )
    const savedItems = saved.map((view) => ({ key: `saved:${view.id}`, name: view.name, configuration: view.configuration, view }))
    const matches = (item) => sameTableView(item.configuration, tableState, availableColumns, currentLocation)
    const inferred = savedItems.find(matches) ?? null
    const requestedView = savedItems.find((item) => String(item.view.id) === tableState.viewId)
    const active = requestedView ?? selected ?? inferred
    const modified = Boolean(active && !matches(active))

    // Retain the selected identity when filters, columns or sorting change.
    useEffect(() => {
        if (!selected && inferred && tableState.page && !isLoading) setSelected(inferred)
    }, [selected, tableState, isLoading, inferred])

    useEffect(() => {
        if (!isLoading && !error && selected && !saved.some((view) => view.id === selected.view.id)) {
            setSelected(null)
        }
    }, [saved, selected, isLoading, error])

    const choose = (item) => {
        selectView(item.configuration, item.view.id)
        setSelected(item)
        setStale(false)
        setErrorMessage('')
        setOpened(false)
    }
    const beginEdit = (view = null, replace = false) => {
        setOpened(false)
        setDeleting(null)
        setModalOpened(true)
        setErrorMessage('')
        setEditor({
            showInNavigation: Boolean(view?.show_in_navigation),
            view, name: view?.name ?? '', visibility: view?.visibility ?? 'personal',
            includeLocation: view?.configuration.location_id != null,
            configuration: view && !replace ? structuredClone(view.configuration) : tableViewConfiguration(tableState, availableColumns),
            location: view && !replace ? view.configuration.location_id ?? currentLocation : currentLocation,
        })
    }
    const reportError = async (error) => {
        const conflict = error?.response?.status === 409
        setErrorMessage(conflict
            ? 'Cette vue a changé depuis son ouverture. Rechargez-la avant de réessayer.'
            : 'Impossible d’enregistrer la modification. Réessayez.')
        if (conflict) {
            setStale(true)
            await mutate().catch(() => {})
        }
    }
    const persist = async (view, payload) => {
        const response = view
            ? await requestPatch(`/table-views/${view.id}`, { ...payload, revision: view.revision })
            : await requestPost('/table-views', payload)
        const updated = response.data
        await mutate((items = []) => [...items.filter((item) => item.id !== updated.id), updated]
            .sort(compareTableViews), { revalidate: false })
        if (!view || active?.view?.id === view.id) {
            setSelected({ key: `saved:${updated.id}`, name: updated.name, configuration: updated.configuration, view: updated })
            selectView(updated.configuration, updated.id)
        }
        await refreshNavigation()
        setStale(false)
    }
    const save = async (event) => {
        event.preventDefault()
        setBusy(true)
        setErrorMessage('')
        const configuration = structuredClone(editor.configuration)
        delete configuration.location_id
        if (withLocation && editor.includeLocation) {
            configuration.location_id = String(editor.location)
        }
        try {
            await persist(editor.view, { name: editor.name.trim(), visibility: editor.visibility, table_key: tableKey, configuration, show_in_navigation: editor.showInNavigation })
            setModalOpened(false)
        } catch (error) { await reportError(error) }
        finally { setBusy(false) }
    }
    const saveChanges = async () => {
        if (active.view.visibility === 'shared') {
            beginEdit(active.view, true)
            return
        }
        setBusy(true)
        setErrorMessage('')
        try {
            await persist(active.view, {
                name: active.view.name, visibility: active.view.visibility, table_key: tableKey,
                configuration: tableViewConfiguration(tableState, availableColumns, active.configuration.location_id != null, currentLocation),
            })
        } catch (error) { await reportError(error) }
        finally { setBusy(false) }
    }
    const remove = async () => {
        setBusy(true)
        setErrorMessage('')
        try {
            await requestDelete(`/table-views/${deleting.id}?revision=${deleting.revision}`)
            await mutate((items = []) => items.filter((item) => item.id !== deleting.id), { revalidate: false })
            await refreshNavigation()
            if (active?.view?.id === deleting.id) setSelected(null)
            setModalOpened(false)
        } catch (error) { await reportError(error) }
        finally { setBusy(false) }
    }
    const saveOrder = async () => {
        const groups = ['shared', 'personal'].filter((visibility) => visibility === 'personal' || role === 'ADMIN').flatMap((visibility) => {
            const views = ordering.filter((view) => view.visibility === visibility)
            const previous = orderBaseline.filter((view) => view.visibility === visibility)
            if (views.map((view) => view.id).join(',') === previous.map((view) => view.id).join(',')) return []
            return [{ visibility, views: views.map(({ id, revision }) => ({ id, revision })) }]
        })
        if (!groups.length) { setOrdering(null); return }
        setSavingOrder(true)
        setErrorMessage('')
        try {
            const { data: updated } = await requestPost('/table-views/reorder', { table_key: tableKey, groups })
            await mutate(updated, { revalidate: false })
            if (selected) {
                const view = updated.find((view) => view.id === selected.view.id)
                if (view) setSelected({ key: `saved:${view.id}`, name: view.name, configuration: view.configuration, view })
            }
            await refreshNavigation()
            setOrdering(null)
        } catch (error) {
            await reportError(error)
            if (error?.response?.status === 409) setOrdering(null)
        } finally { setSavingOrder(false) }
    }
    const close = () => {
        if (busy) return
        setOpened(false)
        setModalOpened(false)
    }
    const finishClosing = () => {
        if (modalOpened) return
        setEditor(null)
        setDeleting(null)
        setErrorMessage('')
    }
    const reloadActive = () => {
        const latest = savedItems.find((item) => item.key === active?.key)
        if (latest) choose(latest)
    }
    if (!canSave) return null

    return <>
        <Popover opened={opened} onChange={(value) => { if (!savingOrder) { setOpened(value); if (!value) { setOrdering(null); setActionMenuId(null) } } }} position="bottom-end" width={360} shadow="md" radius="lg" withinPortal closeOnClickOutside={actionMenuId == null} returnFocus>
            <Popover.Target>
                <Tooltip label="Vues enregistrées" disabled={opened} events={{ hover: true, focus: true, touch: false }}>
                <ActionIcon size={35} variant={opened ? 'light' : 'subtle'} color="gray" radius="xl" disabled={busy || savingOrder}
                    aria-label={`Vues : ${(active?.name ?? 'Vue actuelle')}${modified ? ', modifiée' : ''}`}
                    onClick={() => setOpened((value) => !value)}>
                    <IconBookmark size={19} />
                </ActionIcon>
                </Tooltip>
            </Popover.Target>
            <Popover.Dropdown p={8} style={{ maxWidth: 'calc(100vw - 24px)' }}>
                <div className="flex items-center justify-between px-2 pb-2 pt-1">
                    <span className="text-sm text-gray-500">{ordering ? 'Ordre des vues' : 'Vues'}</span>
                    {!ordering && saved.length > 1 && (role === 'ADMIN' || saved.filter((view) => view.visibility === 'personal').length > 1) &&
                        <Button variant="subtle" color="gray" size="compact-xs" disabled={busy} onClick={() => { setOrdering([...saved]); setOrderBaseline([...saved]); setErrorMessage('') }}>Réorganiser</Button>}
                </div>
                {ordering ? <>
                    <div className="max-h-[min(380px,55dvh)] overflow-y-auto overscroll-contain">
                        <fieldset disabled={savingOrder} className="min-w-0 border-0 p-0">
                            <TableViewOrder views={ordering} onChange={setOrdering} isAdmin={role === 'ADMIN'} />
                        </fieldset>
                    </div>
                    <div className="flex justify-end gap-2 border-t pt-3">
                        <Button variant="default" size="xs" disabled={savingOrder} onClick={() => { setOrdering(null); setErrorMessage('') }}>Annuler</Button>
                        <Button size="xs" loading={savingOrder} onClick={saveOrder}>Enregistrer</Button>
                    </div>
                </> : <>
                <ScrollArea.Autosize mah="min(380px,55dvh)" scrollbars="y"
                    classNames={{ viewport: '[&>div]:block! [&>div]:w-full [&>div]:min-w-0!' }}>
                    {savedItems.map((item) => <div key={item.key}
                        className={`group flex w-full min-w-0 items-center gap-1 rounded-md px-2 ${active?.key === item.key ? 'bg-gray-100' : 'hover:bg-gray-50'}`}>
                        <button type="button" aria-current={active?.key === item.key ? 'true' : undefined}
                            className="min-w-0 flex-1 rounded-md py-3 text-left text-sm focus-visible:outline-2 focus-visible:outline-baltic"
                            onClick={() => choose(item)}>
                            <span className="block max-w-full truncate" title={item.name}>{item.name}</span>
                            {active?.key === item.key && modified && <span className="text-xs text-gray-500">Modifiée</span>}
                        </button>
                        {item.view && <Menu position="bottom-end" withinPortal zIndex={350}
                            opened={actionMenuId === item.view.id}
                            onChange={(value) => setActionMenuId(value ? item.view.id : null)}>
                            <Menu.Target><ActionIcon className="shrink-0" variant="subtle" color="gray" aria-label={`Actions pour ${item.name}`}><IconDots size={17} /></ActionIcon></Menu.Target>
                            <Menu.Dropdown>
                                <Menu.Item leftSection={<IconPencil size={16} />} onClick={() => beginEdit(item.view)}>Modifier la vue</Menu.Item>
                                <Menu.Item color="red" leftSection={<IconTrash size={16} />} onClick={() => { setOpened(false); setErrorMessage(''); setEditor(null); setDeleting(item.view); setModalOpened(true) }}>Supprimer</Menu.Item>
                            </Menu.Dropdown>
                        </Menu>}
                    </div>)}
                    {isLoading && <p role="status" className="px-2 py-3 text-sm text-gray-500">Chargement…</p>}
                    {error && <Button variant="subtle" color="red" size="compact-sm" onClick={() => mutate()}>Réessayer le chargement</Button>}
                </ScrollArea.Autosize>
                {!isLoading && !error && !savedItems.length && <p className="px-2 py-3 text-sm text-gray-500">Aucune vue enregistrée.</p>}
                {modified && canSave && active?.view && <div className="px-2 pt-3">
                    {stale ? <Button variant="subtle" size="compact-sm" disabled={busy} onClick={reloadActive}>Recharger la vue</Button>
                        : <Button variant="subtle" size="compact-sm" loading={busy} onClick={saveChanges}>Enregistrer les modifications</Button>}
                </div>}
                {canSave && <button type="button" disabled={busy || !tableState.page} onClick={() => beginEdit()}
                    className="mt-2 flex w-full items-center gap-2 rounded-md px-2 py-3 text-left text-sm underline underline-offset-4 hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-baltic disabled:opacity-50">
                    <IconPlus size={17} />Ajouter une vue personnalisée
                </button>}
                </>}
                {errorMessage && !editor && !deleting && <Alert mt="sm" color="red" role="alert">{errorMessage}</Alert>}
            </Popover.Dropdown>
        </Popover>
        <Modal
            opened={modalOpened} onClose={close} onExitTransitionEnd={finishClosing}
            title={editor ? (editor.view ? 'Modifier la vue' : 'Ajouter une vue personnalisée') : `Supprimer « ${deleting?.name} » ?`}
            size={520} centered
            closeOnEscape={!busy} closeOnClickOutside={!busy} withCloseButton={!busy}
        >
            {editor && <form onSubmit={save} className="flex w-full min-w-0 flex-col gap-5">
                <TextInput label="Nom" placeholder="Ex. Suivi des têtes de montre" data-autofocus required maxLength={80} value={editor.name} onChange={(event) => setEditor({ ...editor, name: event.currentTarget.value })} disabled={busy} />
                <fieldset className="min-w-0 border-0 p-0">
                    <legend className="mb-2 text-sm font-medium">Accès</legend>
                    <SegmentedControl fullWidth radius="md" size="sm" value={editor.visibility}
                        aria-label="Accès à la vue"
                        disabled={busy || Boolean(editor.view && editor.view.owner_id !== userId)}
                        data={[
                            { value: 'personal', label: <span className="flex items-center justify-center gap-2 py-1"><IconLock size={16} aria-hidden="true" />Moi uniquement</span> },
                            { value: 'shared', label: <span className="flex items-center justify-center gap-2 py-1"><IconUsers size={16} aria-hidden="true" />Équipe</span> },
                        ]}
                        onChange={(visibility) => setEditor((previous) => ({ ...previous, visibility }))} />
                    <p className="mt-2 text-xs text-gray-500">{editor.visibility === 'shared'
                        ? 'Visible et modifiable par l’équipe.'
                        : 'Visible uniquement par vous.'}</p>
                    <div className="mt-3 flex items-center justify-between gap-4 border-t pt-3">
                        <div className="min-w-0">
                            <label htmlFor="view-navigation" className="block text-sm">Afficher dans la navigation</label>
                            <p className="mt-0.5 text-xs text-gray-500">{editor.visibility === 'shared' ? 'Pour toute l’équipe.' : 'Pour vous uniquement.'}</p>
                        </div>
                        <Switch id="view-navigation" aria-label="Afficher dans la navigation" className="shrink-0"
                            checked={editor.showInNavigation} disabled={busy}
                            onChange={(event) => setEditor({ ...editor, showInNavigation: event.currentTarget.checked })} />
                    </div>
                </fieldset>
                <fieldset disabled={busy} className="min-w-0 border-0 p-0">
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <span className="text-xs font-medium text-gray-500">Réglages de la vue</span>
                        {editor.view && <Button variant="subtle" color="gray" size="compact-xs" onClick={() => setEditor((previous) => ({ ...previous,
                            configuration: tableViewConfiguration(tableState, availableColumns), location: currentLocation,
                        }))}>Reprendre le tableau actuel</Button>}
                    </div>
                    <Accordion variant="default" styles={{ control: { padding: 0 }, label: { padding: '12px 0', fontSize: 14 }, content: { padding: '0 0 12px' } }}>
                        <Accordion.Item value="columns">
                            <Accordion.Control>Colonnes <span className="ml-1 text-gray-500">{orderedTableColumns(availableColumns, editor.configuration.columns).length} affichées</span></Accordion.Control>
                            <Accordion.Panel>
                                <TableColumns inline availableColumns={availableColumns} tableState={editor.configuration}
                                    changeColumns={(columns, columnOrder) => setEditor((previous) => ({ ...previous, configuration: { ...previous.configuration, columns, columnOrder } }))} />
                            </Accordion.Panel>
                        </Accordion.Item>
                    </Accordion>
                    <TableViewFilters availableFilters={availableFilters} filters={editor.configuration.filters ?? {}}
                        changeFilters={(filters) => setEditor((previous) => ({ ...previous, configuration: { ...previous.configuration, filters } }))} />
                    <div className="mt-3 grid grid-cols-2 gap-3">
                        <Select label="Trier par" value={editor.configuration.orderBy}
                            data={[...availableColumns.filter((column) => column.sortable || column.key === editor.configuration.orderBy).map((column) => ({ value: column.key, label: column.name })),
                                ...(!availableColumns.some((column) => column.key === editor.configuration.orderBy) ? [{ value: editor.configuration.orderBy, label: 'Ordre initial' }] : [])]}
                            allowDeselect={false} comboboxProps={{ withinPortal: false }}
                            onChange={(orderBy) => setEditor((previous) => ({ ...previous, configuration: { ...previous.configuration, orderBy } }))} />
                        <Select label="Ordre" value={editor.configuration.orderByDirection}
                            data={[{ value: 'asc', label: 'Croissant' }, { value: 'desc', label: 'Décroissant' }]}
                            allowDeselect={false} comboboxProps={{ withinPortal: false }}
                            onChange={(orderByDirection) => setEditor((previous) => ({ ...previous, configuration: { ...previous.configuration, orderByDirection } }))} />
                    </div>
                    {withLocation && <div className="mt-4 border-t pt-3">
                        <Switch label="Fixer le lieu" checked={editor.includeLocation} disabled={busy} onChange={(event) => setEditor({ ...editor, includeLocation: event.currentTarget.checked })} />
                        {editor.includeLocation && <div className="mt-3"><LocationSelect aria-label="Lieu mémorisé" value={editor.location}
                            onlyProductComponentsLocations={tableKey === 'production-orders'} withAll={editor.location === 'all'}
                            disabled={busy || role !== 'ADMIN'} comboboxProps={{ withinPortal: false }}
                            onChange={(location) => setEditor((previous) => ({ ...previous, location }))} /></div>}
                    </div>}
                </fieldset>
                {editor.view && <p className="truncate text-xs text-gray-500" title={editor.view.last_editor?.name ? `Dernière modification par ${editor.view.last_editor.name}` : undefined}>
                    {editor.view.owner?.name ? `Créée par ${editor.view.owner.name}` : 'Importée du backoffice'}
                </p>}
                {errorMessage && <Alert color="red" role="alert">{errorMessage}</Alert>}
                <div className="flex justify-end gap-2 border-t pt-4">
                    <Button type="button" variant="default" disabled={busy} onClick={close}>Annuler</Button>
                    <Button type="submit" loading={busy} disabled={!editor.name.trim()}>Enregistrer</Button>
                </div>
            </form>}
            {deleting && <div className="flex flex-col gap-4">
                <p className="text-sm">{deleting.visibility === 'shared' ? 'Cette vue sera supprimée pour toute l’équipe.' : 'Cette vue personnelle sera supprimée.'}</p>
                {errorMessage && <Alert color="red" role="alert">{errorMessage}</Alert>}
                <div className="flex justify-end gap-2">
                    <Button variant="default" disabled={busy} onClick={close}>Annuler</Button>
                    <Button color="red" loading={busy} onClick={remove}>Supprimer</Button>
                </div>
            </div>}
        </Modal>
    </>
}
